import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errorText } from "@/lib/api";
import { useAgent } from "@/hooks/useAgent";
import { can } from "@/lib/roles";
import { copyText, gbp, shortDate } from "@/lib/format";
import { documentRows, incomeRows, metSummary, payRows } from "@/lib/verdict";
import { AgentLayout } from "@/components/agent/AgentLayout";
import { PropertyImage } from "@/components/agent/PropertyImage";
import { ConfirmDialog, FieldRow, RequestInfoDialog, Section } from "@/components/agent/profile";
import { DecisionTag, EmptyState, FraudTag, PetsTag, ListingTag, Loading, PageHeader, PrimaryButton, ProgressTag, SecondaryButton, VerdictTag } from "@/components/agent/ui";

const Rows = ({ rows, prefix }) => rows.map(({ key, ...r }) => <FieldRow key={key} {...r} testid={`${prefix}-${key}`} />);

const Application = ({ a, l, rented }) => (
  <Section title="Application" testid="profile-application">
    <FieldRow label={<span data-testid="profile-address">{l.address}</span>} detail={`${gbp(l.monthly_rent)} a month`}
      lead={<span className="h-10 w-14 shrink-0 overflow-hidden rounded-md"><PropertyImage src={l.image_data} /></span>}
      trail={<ListingTag status={l.listing_status} />} />
    {a.identity?.status === "verified" && (
      <FieldRow label="Name" detail="Verified by Stripe from their ID and a live selfie" value={a.identity.verified_name || "—"} status="passed" testid="profile-identity" />
    )}
    {a.contact && (
      <FieldRow label="Contact" detail="Email and mobile confirmed with a code" value={`${a.contact.email} · ${a.contact.phone}`} testid="profile-contact" />
    )}
    {a.identity?.status === "failed" && (
      <FieldRow label="ID check" detail={a.identity.error || "Stripe couldn't verify this person"} value="Failed" status="failed" testid="profile-identity" />
    )}
    {a.pets_flag && (
      <FieldRow label="Pets" detail="The applicant has pets, and this property doesn't allow them." value="Has pets" status="failed" testid="profile-pets" />
    )}
    {a.decision && (
      <FieldRow label={a.decision.decision === "accepted" ? "Accepted" : "Declined"} detail={a.decision.reason || undefined}
        value={shortDate(a.decision.timestamp)} testid="profile-decision-row" />
    )}
    {rented && a.decision?.decision !== "accepted" && (
      <p className="text-sm text-muted-foreground" data-testid="already-rented-note">This property is already rented, so no one else can be accepted.</p>
    )}
    {!a.submitted_at && <p className="text-sm text-muted-foreground" data-testid="profile-not-submitted">The applicant hasn't submitted yet. Pay received shows once they submit.</p>}
  </Section>
);

const PayReceived = ({ a, l }) => {
  const rows = payRows(a, l);
  return (
    <Section id="flagged-evidence" rootClassName="scroll-mt-6" title="Pay received" testid="profile-evidence" summary={metSummary(rows)}
      description={a.fraud_flag ? "A payment differs from the payslip by more than 10%. Worth a closer look." : "Each payslip's net pay should arrive in the bank statements."}>
      <Rows rows={rows} prefix="profile-pay" />
      {(a.income_transactions || []).map((t, i) => (
        <FieldRow key={`t-${i}`} label={t.payer || "Bank credit"} detail={`Income payment · ${shortDate(t.date)}`} value={gbp(t.amount, 2)} testid={`profile-income-${i}`} />
      ))}
    </Section>
  );
};

const Extras = ({ a }) => (
  <>
    {(a.notes || []).length > 0 && (
      <Section title="Applicant notes" description="What the applicant explained about a document." testid="profile-notes">
        {a.notes.map((n, i) => <FieldRow key={i} label={n.item} detail={n.note} testid={`profile-note-${i}`} />)}
      </Section>
    )}
    {(a.info_requests || []).length > 0 && (
      <Section title="Info requests" testid="profile-requests">
        {a.info_requests.map((r, i) => (
          <FieldRow key={r.id} label={r.step_label} detail={r.message} value={r.resolved_at ? "Fixed" : r.email_sent ? "Emailed" : "Not emailed"}
            status={r.resolved_at ? "passed" : "pending"} testid={`profile-request-${i}`} />
        ))}
      </Section>
    )}
  </>
);

export default function ApplicantProfile() {
  const { letId, applicantId } = useParams();
  const agent = useAgent();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [dialog, setDialog] = useState(null);

  const load = useCallback(async () => {
    try {
      setData((await api.get(`/lets/${letId}/shortlist`)).data);
      setError("");
    } catch (err) {
      setError(errorText(err));
    }
  }, [letId]);

  useEffect(() => {
    load();
    api.post(`/lets/${letId}/applicants/${applicantId}/open`).catch(() => {});
  }, [load, letId, applicantId]);

  const a = data?.applicants.find((x) => x.id === applicantId);
  const l = data?.let;
  const call = async (fn) => {
    try {
      await fn();
    } catch (err) {
      toast.error(errorText(err));
    }
  };

  const requestInfo = (step, message) => call(async () => {
    const { data: r } = await api.post(`/lets/${letId}/applicants/${applicantId}/request-info`, { step, message });
    if (r.email_sent) toast.success("Request sent by email");
    else {
      await copyText(r.link);
      toast.warning("Email couldn't be sent. The fix link is copied, so you can send it yourself.");
    }
    setDialog(null);
    load();
  });
  const decide = (decision, reason = "", emailBody) => call(async () => {
    const { data: r } = await api.post(`/lets/${letId}/applicants/${applicantId}/decision`, { decision, reason, email_body: emailBody });
    const done = decision === "accepted" ? `${a.label} accepted` : `${a.label} declined`;
    if (r.email_sent) toast.success(`${done} · email sent`);
    else toast.warning(`${done}. ${r.email_note || "No email was sent."}`);
    setDialog(null);
    load();
  });

  if (error && !data) return <AgentLayout><EmptyState title="Couldn't load this applicant" text={error} action={<SecondaryButton onClick={load} testid="profile-retry">Try again</SecondaryButton>} testid="profile-error" /></AgentLayout>;
  if (!data) return <AgentLayout><Loading testid="profile-loading" /></AgentLayout>;
  if (!a) return <AgentLayout><EmptyState title="Applicant not found" text="This applicant isn't on this property." action={<SecondaryButton to="/applicants" testid="profile-back">Back to applicants</SecondaryButton>} testid="profile-missing" /></AgentLayout>;

  const rented = l.listing_status === "rented";
  const acceptedHere = a.decision?.decision === "accepted";
  const canAct = can(agent?.role, "decide");
  const complete = a.status === "documents_complete";
  const income = incomeRows(a, l);
  const docs = documentRows(a.checks, complete);

  const badges = (
    <span className="mt-1 flex flex-wrap items-center gap-1.5">
      <ProgressTag status={a.status} testid="profile-progress" />
      <VerdictTag status={a.status} outcome={a.outcome} testid="profile-verdict" />
      {a.fraud_flag && <a href="#flagged-evidence" data-testid="profile-fraud-link"><FraudTag /></a>}
      {a.pets_flag && <PetsTag testid="profile-pets-tag" />}
      <DecisionTag decision={a.decision} testid="profile-decision" />
    </span>
  );
  const actions = canAct && (
    <div className="flex items-center gap-2">
      <SecondaryButton onClick={() => setDialog("request")} disabled={!a.submitted_at} testid="request-info-button">Request info</SecondaryButton>
      <SecondaryButton onClick={() => setDialog("decline")} testid="decline-button">Decline</SecondaryButton>
      <PrimaryButton onClick={() => setDialog("accept")} disabled={rented} testid="accept-button">{acceptedHere ? "Accepted" : "Accept"}</PrimaryButton>
    </div>
  );

  return (
    <AgentLayout>
      <PageHeader back={{ to: "/applicants", label: "Applicants" }} title={a.label} description={badges} action={actions} />
      <Application a={a} l={l} rented={rented} />
      <Section title="Income" description={`At least ${a.income_multiplier ?? l.criteria.income_multiplier} × the monthly rent, before tax.`} summary={metSummary(income)} testid="profile-income">
        <Rows rows={income} prefix="profile-income" />
      </Section>
      <PayReceived a={a} l={l} />
      <Section title="Documents" description="What Landin checked on every file uploaded." summary={metSummary(docs)} testid="profile-checks-card">
        {docs.length ? docs.map(({ key, ...r }) => <FieldRow key={key} {...r} testid={`profile-check-${key}`} />) : <p className="text-sm text-muted-foreground">No checks yet.</p>}
      </Section>
      <Extras a={a} />

      <ConfirmDialog key={`a-${dialog}`} open={dialog === "accept"} title={`Accept ${a.label}?`} description={`This marks ${l.address} as rented.`}
        confirmLabel="Accept" decision="accepted" previewUrl={`/lets/${letId}/applicants/${applicantId}/decision-email`}
        onClose={() => setDialog(null)} onConfirm={(r, body) => decide("accepted", r, body)} testid="accept-dialog" />
      <ConfirmDialog key={`d-${dialog}`} open={dialog === "decline"} title={`Decline ${a.label}?`} description="Your reason is saved with the decision."
        confirmLabel="Decline" decision="declined" previewUrl={`/lets/${letId}/applicants/${applicantId}/decision-email`} withReason
        onClose={() => setDialog(null)} onConfirm={(r, body) => decide("declined", r, body)} testid="decline-dialog" />
      <RequestInfoDialog key={`r-${dialog}`} open={dialog === "request"} steps={a.steps || []} onClose={() => setDialog(null)} onSend={requestInfo} />
    </AgentLayout>
  );
}
